import { useCallback, useState } from 'react';
import { apiService } from 'utils/api';
import { getOrderStatus, handleApiError } from 'utils/functions';
import { OrderType, UpdateOrderBrandMeal } from 'models/order.model';
import { ENUM_STATUS } from 'utils/enum';
export const useOrderData = (orderId?: string) => {
  const [order, setOrder] = useState<OrderType>();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [subTotal, setSubTotal] = useState(0);
  const [isOrderUpdated, setIsOrderUpdated] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(true);

  const [updatedOrderDetails, setUpdatedOrderDetails] = useState<{
    subTotal: number;
    serviceCharge: string;
    deliveryFee: string;
    total: number;
  } | null>(null);

  const getOrder = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const res = await apiService.get<{ data: OrderType }>(`orders/code/${orderId}`, {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });

      const orderData = res.data.data;
      const canModifyOrder = getOrderStatus(orderData) === ENUM_STATUS.PENDING;
      setIsProcessingOrder(canModifyOrder);

      if (!orderData || !canModifyOrder) return;
      setSubTotal(
        Number(orderData.calculated_order.total_amount) -
          Number(orderData.calculated_order.delivery_fee) -
          Number(orderData.calculated_order.service_charge)
      );
      setOrder(orderData);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const transformOrderMealsToPayload = (orderMeals: UpdateOrderBrandMeal[]) => {
    const meals = orderMeals.flatMap((brandOrder) =>
      brandOrder.meals.map((meal) => ({
        id: meal.id,
        quantity: meal.quantity,
        order_note: meal.order_note || '',
        addons: meal.addons.map((addon) => ({
          id: addon.id,
          quantity: addon.quantity
        }))
      }))
    );

    return { meals };
  };

  const updateOrder = async (orderMeals: UpdateOrderBrandMeal[]) => {
    if (!order?.id || !orderMeals.length) return;
    setUpdating(true);
    try {
      const payload = transformOrderMealsToPayload(orderMeals);
      const response = await apiService.patch(`patch-order/${order.id}`, {
        data: payload,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const details = response.data.data.calculated_order;
      setUpdatedOrderDetails({
        total: Number(details.total_amount),
        serviceCharge: details.service_charge,
        deliveryFee: details.delivery_fee,
        subTotal:
          Number(details.total_amount) -
          (Number(details.service_charge) + Number(details.delivery_fee))
      });
      setIsOrderUpdated(true);
      return true;
    } catch (error) {
      handleApiError(error);
    } finally {
      setUpdating(false);
    }
  };

  return {
    order,
    loading,
    updating,
    getOrder,
    updateOrder,
    subTotal,
    updatedOrderDetails,
    isOrderUpdated,
    isProcessingOrder
  };
};
